import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Flag, PageHead, Panel } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { countryInfo } from "@/lib/countries";
import { type ServiceCatalog } from "@/lib/service-catalog";
import { ErrorBox, SkeletonRows, Tag, useServer } from "@/features/finance/kit";

export default function AvailableMarkets() {
  const catalog = useServer<ServiceCatalog>("/dashboard/services");
  const [search, setSearch] = useState("");
  const services = catalog.data?.services ?? [];
  const filtered = useMemo(
    () =>
      services.filter((s) =>
        [
          s.country?.code,
          s.country?.name,
          countryInfo(s.country?.code ?? "")?.name,
          s.currency?.code,
          s.currency?.name,
          s.operation,
          s.payment_method,
        ]
          .join(" ")
          .toLocaleLowerCase("fr")
          .includes(search.toLocaleLowerCase("fr")),
      ),
    [services, search],
  );
  return (
    <>
      <PageHead
        title="Pays, monnaies et services"
        sub="Disponibilité renvoyée par l’API pour votre environnement. Ce catalogue ne vaut pas autorisation d’accès à un pays."
      >
        <Button variant="outline" onClick={() => catalog.reload()}>
          Actualiser
        </Button>
      </PageHead>
      <Panel className="space-y-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            className="max-w-md"
            aria-label="Filtrer les services"
            placeholder="Pays, monnaie ou moyen de paiement…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {catalog.data?.environment && (
            <Tag value={catalog.data.environment} />
          )}
          <Link
            href="/wallets"
            className="text-sm font-medium text-primary underline"
          >
            Accès pays et création de wallets
          </Link>
        </div>
        {catalog.loading ? (
          <SkeletonRows n={5} />
        ) : catalog.error ? (
          <ErrorBox error={catalog.error} onRetry={catalog.reload} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  {[
                    "Pays",
                    "Monnaie",
                    "Opération",
                    "Moyen de paiement",
                    "Disponibilité",
                  ].map((h) => (
                    <th key={h} className="whitespace-nowrap px-3 py-3">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr
                    key={`${s.country?.code}-${s.currency?.code}-${s.operation}-${s.payment_method}-${i}`}
                    className="border-b"
                  >
                    <td className="px-3 py-3">
                      <span className="flex items-center gap-2">
                        <Flag code={s.country?.code ?? "INT"} />
                        {countryInfo(s.country?.code ?? "")?.name ||
                          s.country?.name ||
                          s.country?.code}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <b>{s.currency?.code}</b>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {s.currency?.name}
                      </span>
                    </td>
                    <td className="px-3 py-3">{s.operation}</td>
                    <td className="px-3 py-3">{s.payment_method}</td>
                    <td className="px-3 py-3">
                      {s.available === true ? "Disponible" : "Indisponible"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filtered.length && (
              <p className="py-6 text-sm text-muted-foreground">
                {services.length
                  ? "Aucun service ne correspond à votre recherche."
                  : "Aucun service renvoyé par le backend."}
              </p>
            )}
          </div>
        )}
      </Panel>
    </>
  );
}
